import { useState, useEffect } from 'react';
import { useFrameworkChangeSets, type FrameworkChangeSet } from '@/hooks/useFrameworkChanges';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { cn } from '@/lib/utils';
import { ChangeSetDetailView } from './ChangeSetDetailView';

export function FrameworkApprovalsTab() {
  const { data: openSets } = useFrameworkChangeSets('open');
  const { data: submittedSets, isLoading: loadingSubmitted } = useFrameworkChangeSets('submitted');
  const { data: approvedSets } = useFrameworkChangeSets('approved');
  const [selectedSetId, setSelectedSetId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'submitted' | 'open' | 'approved'>('submitted');

  const setsByFilter: Record<typeof statusFilter, FrameworkChangeSet[]> = {
    submitted: submittedSets?.changeSets || [],
    open: openSets?.changeSets || [],
    approved: approvedSets?.changeSets || [],
  };

  const visibleSets = setsByFilter[statusFilter];

  // Auto-select first visible set
  useEffect(() => {
    if (visibleSets.length > 0 && !visibleSets.some(s => s.id === selectedSetId)) {
      setSelectedSetId(visibleSets[0].id);
    } else if (visibleSets.length === 0) {
      setSelectedSetId(null);
    }
  }, [visibleSets, selectedSetId]);

  if (loadingSubmitted) {
    return (
      <div className="flex h-32 items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-medium text-gray-900">Services Framework change sets</h3>
        <p className="text-xs text-gray-500 mt-1">
          Review and approve Services Framework changes. Approved sets cascade to draft projects only — locked
          (submitted/approved) projects are unaffected.
        </p>
      </div>

      <div className="flex gap-2">
        {(['submitted', 'open', 'approved'] as const).map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={cn(
              'px-3 py-1.5 text-xs font-medium rounded-md border transition-colors',
              statusFilter === s
                ? 'bg-xtravirt-navy text-white border-xtravirt-navy'
                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
            )}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)} ({setsByFilter[s].length})
          </button>
        ))}
      </div>

      <div className="flex gap-4 h-[calc(100vh-340px)] min-h-[400px]">
        {/* List of change sets */}
        <div className="w-72 flex-shrink-0 flex flex-col bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="p-3 border-b border-gray-200 bg-gray-50">
            <h4 className="font-semibold text-gray-900 text-sm">Change sets</h4>
          </div>
          <div className="flex-1 overflow-y-auto">
            {visibleSets.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">No {statusFilter} change sets</div>
            ) : (
              visibleSets.map((cs) => (
                <button
                  key={cs.id}
                  onClick={() => setSelectedSetId(cs.id)}
                  className={cn(
                    'w-full text-left px-3 py-2.5 border-b border-gray-100 transition-colors',
                    selectedSetId === cs.id ? 'bg-blue-50' : 'hover:bg-gray-50'
                  )}
                >
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {cs.title || '(Untitled)'}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5 truncate">
                    by {cs.createdBy}
                  </div>
                  <div className="text-[10px] text-gray-400 mt-0.5">
                    {cs.submittedAt
                      ? `Submitted ${new Date(cs.submittedAt).toLocaleDateString()}`
                      : `Created ${new Date(cs.createdAt).toLocaleDateString()}`}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Detail panel */}
        <div className="flex-1 flex flex-col bg-white rounded-lg border border-gray-200 overflow-hidden">
          {selectedSetId ? (
            <ChangeSetDetailView setId={selectedSetId} />
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-500 text-sm">
              Select a change set to review
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
